import React from 'react';
import { MMLogoMark } from '@/components/brand/mm-logo';
import { cn } from '@/lib/utils';

/**
 * Ambient bees for the report pages: three at work, looping slowly behind the
 * page header toward a small comb on the right. Low opacity, pointer-inert,
 * and it stays out of the copy's way - a motif in the header's dead space,
 * never a wallpaper behind numbers.
 *
 * Pure CSS keyframes on inline SVG, like the sign-in bee, so it costs nothing
 * on a backgrounded tab. Under prefers-reduced-motion the bees park by the
 * comb and the wings stop; the art is still there, it just does not move.
 */
const AMBER = '#E09400';
const HONEY = '#F5C451';
const S3 = Math.sqrt(3);
const R = 13;
const hex = (cx, cy, r) => {
  const p = [];
  for (let i = 0; i < 6; i += 1) { const a = (Math.PI / 3) * i + Math.PI / 6; p.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`); }
  return `M${p.join('L')}Z`;
};
// Six cells, two rows of three, like the sign-in comb.
const CX = 318, CY = 24;
const CELLS = [[CX, CY], [CX + S3 * R, CY], [CX + 2 * S3 * R, CY], [CX + (S3 * R) / 2, CY + 1.5 * R], [CX + (3 * S3 * R) / 2, CY + 1.5 * R], [CX + (5 * S3 * R) / 2, CY + 1.5 * R]];

// Three flight paths, each a slow loop that ends near the comb. Keyframes
// carry translate + a little rotation so the bee banks into its turns.
const CSS = `
.bf-wrap { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.bf-svg { position: absolute; right: 0; top: 50%; transform: translateY(-50%); width: 420px; height: 96px; opacity: .7; }
.bf-cell { fill: none; stroke: ${AMBER}; stroke-width: 1.4; stroke-linejoin: round; opacity: .55 }
.bf-fill { fill: ${HONEY}; opacity: .8 }
.bf-bee { transform-box: fill-box; transform-origin: center; }
@keyframes bf-fly-0 { 0% { transform: translate(20px, 60px) rotate(-8deg) } 25% { transform: translate(110px, 22px) rotate(6deg) } 50% { transform: translate(210px, 58px) rotate(-6deg) } 75% { transform: translate(290px, 30px) rotate(4deg) } 100% { transform: translate(20px, 60px) rotate(-8deg) } }
@keyframes bf-fly-1 { 0% { transform: translate(60px, 18px) rotate(6deg) } 30% { transform: translate(160px, 66px) rotate(-8deg) } 60% { transform: translate(250px, 24px) rotate(6deg) } 100% { transform: translate(60px, 18px) rotate(6deg) } }
@keyframes bf-fly-2 { 0% { transform: translate(140px, 40px) rotate(0deg) } 35% { transform: translate(40px, 72px) rotate(-10deg) } 70% { transform: translate(230px, 70px) rotate(8deg) } 100% { transform: translate(140px, 40px) rotate(0deg) } }
@keyframes bf-wing { from { transform: scaleY(1) } to { transform: scaleY(.35) } }
@media (prefers-reduced-motion: reduce) {
  .bf-b0 { transform: translate(262px, 62px) rotate(-8deg) }
  .bf-b1 { transform: translate(236px, 74px) rotate(6deg) }
  .bf-b2 { transform: translate(288px, 78px) rotate(0deg) }
}
@media (prefers-reduced-motion: no-preference) {
  .bf-b0 { animation: bf-fly-0 38s ease-in-out infinite }
  .bf-b1 { animation: bf-fly-1 46s ease-in-out infinite; animation-delay: -12s }
  .bf-b2 { animation: bf-fly-2 52s ease-in-out infinite; animation-delay: -30s }
  .bf-wing { animation: bf-wing .14s ease-in-out infinite alternate; transform-box: fill-box; transform-origin: bottom center }
}
`;

// The bee is the brand's own (components/brand/mm-logo), never a redrawing
// (owner, 23 Oct 2026: one logo, one bee). Centred on its origin.
function Bee({ scale = 0.62 }) {
  return (
    <g transform={`scale(${scale}) translate(-12 -12)`}>
      <MMLogoMark size={24} animate={false} />
    </g>
  );
}

export function BeeFlight({ className }) {
  return (
    <div className={cn('bf-wrap hidden md:block', className)} aria-hidden="true">
      <style>{CSS}</style>
      <svg className="bf-svg" viewBox="0 0 420 96" xmlns="http://www.w3.org/2000/svg"
        style={{ WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 18%)', maskImage: 'linear-gradient(90deg, transparent 0, #000 18%)' }}>
        {CELLS.map(([cx, cy], i) => (
          <g key={i}>
            <path className="bf-cell" d={hex(cx, cy, R)} />
            <path className="bf-fill" d={hex(cx, cy, R * 0.7)} style={{ opacity: [0.9, 0.75, 0.6, 0.5, 0.35, 0.2][i] }} />
          </g>
        ))}
        <g className="bf-bee bf-b0"><Bee /></g>
        <g className="bf-bee bf-b1"><Bee scale={0.52} /></g>
        <g className="bf-bee bf-b2"><Bee scale={0.46} /></g>
      </svg>
    </div>
  );
}

export default BeeFlight;
